import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
  Box,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  SwapVert,
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import "../../tableComponentStyle.css";
import SelectInputComponent from "./SelectInputComponent";
import TextInputComponent from "./TextInputComponent";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const TableComponent = ({
  columns,
  data,
  sortable = true,
  paginated = true,
  selectable = false,
  onRowClick,
  onSelectionChange,
  editOptions = {},
  onCellUpdate,
  onColumnReorder,
  initialSortBy,
  initialSortDirection,
  persistKey,
  groupBy, // Can be null/undefined for no grouping
  renderExpandedContent,
}) => {
  // --- State ---
  const [orderBy, setOrderBy] = useState(() => {
    return (
      localStorage.getItem(`${persistKey}_orderBy`) ||
      initialSortBy ||
      null
    );
  });

  const [order, setOrder] = useState(() => {
    return (
      localStorage.getItem(`${persistKey}_order`) ||
      initialSortDirection ||
      "asc"
    );
  });

  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem(`${persistKey}_page`);
    return savedPage ? parseInt(savedPage, 10) : 0;
  });

  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRowsPerPage = localStorage.getItem(
      `${persistKey}_rowsPerPage`
    );
    return savedRowsPerPage ? parseInt(savedRowsPerPage, 10) : 10;
  });

  const [tableData, setTableData] = useState(data);

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem(`${persistKey}_columnOrder`);
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder);
        if (
          Array.isArray(parsedOrder) &&
          parsedOrder.every((id) =>
            columns.some((col) => col.id === id)
          )
        ) {
          return parsedOrder;
        }
      } catch (error) {
        console.error("Error parsing column order:", error);
      }
    }
    return columns.map((col) => col.id);
  });

  const [columnWidths, setColumnWidths] = useState(() => {
    const savedWidths = localStorage.getItem(
      `${persistKey}_columnWidths`
    );
    return savedWidths ? JSON.parse(savedWidths) : {};
  });

  const [expandedRows, setExpandedRows] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  // --- Persistence Effects ---
  useEffect(() => {
    localStorage.setItem(`${persistKey}_orderBy`, orderBy || "");
  }, [orderBy, persistKey]);

  useEffect(() => {
    localStorage.setItem(`${persistKey}_order`, order);
  }, [order, persistKey]);

  useEffect(() => {
    localStorage.setItem(`${persistKey}_page`, String(page));
  }, [page, persistKey]);

  useEffect(() => {
    localStorage.setItem(
      `${persistKey}_rowsPerPage`,
      String(rowsPerPage)
    );
  }, [rowsPerPage, persistKey]);

  useEffect(() => {
    localStorage.setItem(
      `${persistKey}_columnOrder`,
      JSON.stringify(columnOrder)
    );
    if (onColumnReorder) {
      onColumnReorder(columnOrder);
    }
  }, [columnOrder, onColumnReorder, persistKey]);

  useEffect(() => {
    localStorage.setItem(
      `${persistKey}_columnWidths`,
      JSON.stringify(columnWidths)
    );
  }, [columnWidths, persistKey]);

  useEffect(() => {
    setTableData(data);
    // If the data shrinks and the page is now out-of-range, reset
    if (page * rowsPerPage >= data.length) {
      setPage(0);
    }
  }, [data, page, rowsPerPage]);

  // --- Handlers ---

  const handleSort = (columnId) => {
    if (!sortable) return;
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const handleSelect = (id) => {
    const newSelected = selected.includes(id)
      ? selected.filter((item) => item !== id)
      : [...selected, id];
    setSelected(newSelected);
    if (onSelectionChange) onSelectionChange(newSelected);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllRows = () => {
    setAllExpanded((prev) => !prev);
    setExpandedRows((prev) => {
      if (allExpanded) {
        // Collapse all
        return {};
      }
      // Expand all currently visible rows
      const newExpandedRows = {};
      sortedData.forEach((row) => {
        newExpandedRows[row.id] = true;
      });
      return newExpandedRows;
    });
  };

  // *** EditableCell - With Edit Indicators ***
  const EditableCell = ({ value, rowId, columnId, column }) => {
    const [editing, setEditing] = useState(false);
    const [newValue, setNewValue] = useState(value);
    const [hovered, setHovered] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(null);
    const inputRef = useRef(null);

    const isEditable = column.editable;
    const editorType = column.editorType || "text";
    const options = editOptions[columnId] || [];

    useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus();
      }
    }, [editing]);

    const handleSave = async () => {
      const isDifferent =
        editorType === "multi-select"
          ? JSON.stringify(newValue) !== JSON.stringify(value)
          : newValue !== value;

      if (isDifferent) {
        try {
          await onCellUpdate(rowId, columnId, newValue);
          setUpdateStatus("success");
        } catch (error) {
          console.error("Update failed:", error);
          setUpdateStatus("error");
        }
      }
      setEditing(false);
      setTimeout(() => setUpdateStatus(null), 1500);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      }
      if (e.key === "Escape") {
        setNewValue(value);
        setEditing(false);
      }
    };

    if (!isEditable) {
      return (
        <>
          {Array.isArray(value) ? value.join(", ") : value}
        </>
      );
    }

    return editing ? (
      <Box
        sx={{
          backgroundColor:
            updateStatus === "success"
              ? "#d4edda"
              : updateStatus === "error"
              ? "#f8d7da"
              : "#eef6fc",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          width: "100%",
          height: "100%",
        }}
      >
        {editorType === "select" || editorType === "multi-select" ? (
          <SelectInputComponent
            value={newValue}
            options={options}
            multiple={editorType === "multi-select"}
            onChange={(e) => {
              const incoming = e.target.value;
              setNewValue(
                editorType === "multi-select"
                  ? Array.isArray(incoming)
                    ? incoming
                    : [incoming]
                  : incoming
              );
            }}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            autoFocus
          />
        ) : (
          <TextInputComponent
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            autoFocus
          />
        )}
        {updateStatus === "success" && (
          <CheckCircleIcon
            fontSize="small"
            sx={{ color: "green" }}
          />
        )}
        {updateStatus === "error" && (
          <ErrorIcon fontSize="small" sx={{ color: "red" }} />
        )}
      </Box>
    ) : (
      <Box
        onClick={() => setEditing(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          height: "100%",
        }}
      >
        {Array.isArray(value) ? value.join(", ") : value}
        {hovered && (
          <EditIcon
            fontSize="small"
            sx={{ ml: 0.5, opacity: 0.4 }}
          />
        )}
      </Box>
    );
  };

  // --- Column Reordering (Drag and Drop) ---
  const moveColumn = useCallback(
    (dragIndex, hoverIndex) => {
      const newOrder = [...columnOrder];
      const [movedColumn] = newOrder.splice(dragIndex, 1);
      newOrder.splice(hoverIndex, 0, movedColumn);
      setColumnOrder(newOrder);
    },
    [columnOrder, setColumnOrder]
  );

  const handleResize = useCallback(
    (e, columnId) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startWidth = columnWidths[columnId] || 150;

      const doDrag = (ev) => {
        const width = Math.max(80, startWidth + ev.clientX - startX);
        setColumnWidths((prevWidths) => ({
          ...prevWidths,
          [columnId]: width,
        }));
      };

      const stopDrag = () => {
        document.removeEventListener("mousemove", doDrag);
        document.removeEventListener("mouseup", stopDrag);
      };

      document.addEventListener("mousemove", doDrag);
      document.addEventListener("mouseup", stopDrag);
    },
    [columnWidths, setColumnWidths]
  );

  const DraggableColumn = ({ column, index }) => {
    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: "COLUMN",
        item: { index },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [index]
    );

    const [, drop] = useDrop(
      () => ({
        accept: "COLUMN",
        hover: (item) => {
          if (item.index !== index) {
            moveColumn(item.index, index);
            item.index = index;
          }
        },
      }),
      [index, moveColumn]
    );

    const columnWidth = columnWidths[column.id] || column.width || 150;

    return (
      <TableCell
        ref={(node) => drag(drop(node))}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          cursor: "grab",
          width: columnWidth,
          minWidth: columnWidth,
          maxWidth: columnWidth,
          padding: 0,
          position: "sticky",
          top: 0,
          zIndex: 3,
          backgroundColor: "background.paper",
          borderBottom: "1px solid rgba(148, 163, 184, 0.6)",
        }}
        onClick={() => handleSort(column.id)}
        className={sortable ? "sortable" : ""}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            padding: "8px 12px",
            boxSizing: "border-box",
            fontWeight: 600,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          <span>{column.label}</span>
          {sortable && (
            <SwapVert
              className="sort-icon"
              sx={{
                ml: 0.75,
                fontSize: 18,
                opacity: orderBy === column.id ? 1 : 0.3,
                transform:
                  order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          )}
          {/* Resizer */}
          <div
            className="resizer"
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              width: "10px",
              height: "100%",
              cursor: "col-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseDown={(e) => handleResize(e, column.id)}
          >
            <div
              className="resizer-indicator"
              style={{
                width: "2px",
                height: "50%",
                backgroundColor: "rgba(148,163,184,0.6)",
              }}
            />
          </div>
        </Box>
      </TableCell>
    );
  };

  // --- Sorting helpers ---

  const parseDateValue = (raw) => {
    if (!raw) return null;

    if (raw instanceof Date) return raw.getTime();

    if (typeof raw === "string") {
      // Handle strings like "Dec 4, 2025 • 2:08 PM"
      const cleaned = raw.replace(/\s*•\s*/, " ");
      const ts = Date.parse(cleaned);
      if (!Number.isNaN(ts)) return ts;
    }

    // Fallback: cannot parse
    return null;
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return [...tableData];

    const sortCol = columns.find((c) => c.id === orderBy) || {};
    const sortType = sortCol.sortType || sortCol.type || "string";

    const rows = [...tableData];

    rows.sort((a, b) => {
      const aRaw = a[orderBy];
      const bRaw = b[orderBy];

      let cmp = 0;

      if (sortType === "date") {
        const aTs = parseDateValue(aRaw);
        const bTs = parseDateValue(bRaw);

        if (aTs === null && bTs === null) cmp = 0;
        else if (aTs === null) cmp = -1;
        else if (bTs === null) cmp = 1;
        else cmp = aTs - bTs;
      } else if (sortType === "number") {
        const aNum = aRaw === null || aRaw === undefined ? 0 : Number(aRaw);
        const bNum = bRaw === null || bRaw === undefined ? 0 : Number(bRaw);
        cmp = aNum - bNum;
      } else {
        const aStr =
          aRaw === null || aRaw === undefined
            ? ""
            : String(aRaw).toLowerCase();
        const bStr =
          bRaw === null || bRaw === undefined
            ? ""
            : String(bRaw).toLowerCase();

        if (aStr < bStr) cmp = -1;
        else if (aStr > bStr) cmp = 1;
        else cmp = 0;
      }

      return order === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [orderBy, order, tableData, columns]);

  const totalRows = sortedData.length;

  // Pagination is now applied to the sorted data before grouping
  const pagedData = paginated
    ? sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
    : sortedData;

  // Group the (already paged) data if requested
  let groupedData = {};
  if (groupBy) {
    pagedData.forEach((row) => {
      const groupValue = row[groupBy] ?? "Ungrouped";
      if (!groupedData[groupValue]) {
        groupedData[groupValue] = [];
      }
      groupedData[groupValue].push(row);
    });
  } else {
    groupedData = { "": pagedData };
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "60vh", // makes header sticky relative to this container
          overflow: "auto",
          borderRadius: 2,
          boxShadow: "0 12px 30px rgba(15,23,42,0.12)",
          border: "1px solid rgba(148, 163, 184, 0.4)",
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {groupBy && (
                <TableCell
                  sx={{
                    width: "50px",
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    backgroundColor: "background.paper",
                    borderBottom:
                      "1px solid rgba(148, 163, 184, 0.6)",
                  }}
                >
                  <IconButton size="small" onClick={toggleAllRows}>
                    {allExpanded ? (
                      <KeyboardDoubleArrowUp fontSize="small" />
                    ) : (
                      <KeyboardDoubleArrowDown fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
              )}
              {selectable && (
                <TableCell
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    backgroundColor: "background.paper",
                    borderBottom:
                      "1px solid rgba(148, 163, 184, 0.6)",
                    width: 48,
                  }}
                />
              )}
              {columnOrder.map((colId, index) => {
                const column = columns.find(
                  (col) => col.id === colId
                );
                return column ? (
                  <DraggableColumn
                    key={colId}
                    column={column}
                    index={index}
                  />
                ) : null;
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.entries(groupedData).map(
              ([groupName, groupRows]) => (
                <React.Fragment key={groupName || "no-group"}>
                  {groupBy && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.length +
                          (selectable ? 1 : 0) +
                          (groupBy ? 1 : 0)
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          backgroundColor: "rgba(148,163,184,0.08)",
                          color: "rgba(15,23,42,0.9)",
                        }}
                      >
                        {groupName}
                      </TableCell>
                    </TableRow>
                  )}
                  {groupRows.map((row, rowIndex) => (
                    <React.Fragment key={row.id || rowIndex}>
                      <TableRow
                        hover
                        onClick={
                          onRowClick
                            ? () => onRowClick(row)
                            : null
                        }
                        sx={{
                          cursor: onRowClick ? "pointer" : "default",
                          "&:nth-of-type(odd)": {
                            backgroundColor:
                              "rgba(148, 163, 184, 0.04)",
                          },
                          "&:hover": {
                            backgroundColor:
                              "rgba(59,130,246,0.06)",
                          },
                        }}
                      >
                        {groupBy && (
                          <TableCell
                            sx={{ width: "50px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(row.id)}
                            >
                              {expandedRows[row.id] ? (
                                <KeyboardArrowDown fontSize="small" />
                              ) : (
                                <KeyboardArrowRight fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        )}

                        {selectable && (
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selected.includes(row.id)}
                              onChange={() => handleSelect(row.id)}
                              size="small"
                            />
                          </TableCell>
                        )}

                        {columnOrder.map((colId) => {
                          const column = columns.find(
                            (col) => col.id === colId
                          );
                          const columnWidth =
                            columnWidths[colId] ||
                            column.width ||
                            150;

                          return (
                            <TableCell
                              key={colId}
                              sx={{
                                width: columnWidth,
                                minWidth: columnWidth,
                                maxWidth: columnWidth,
                                borderBottom:
                                  "1px solid rgba(226, 232, 240, 0.9)",
                                fontSize: "0.875rem",
                                color: "rgba(30,41,59,0.9)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                  height: "100%",
                                  padding: "6px 8px",
                                  boxSizing: "border-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {column.editable ? (
                                  <EditableCell
                                    value={row[colId]}
                                    rowId={row.id}
                                    columnId={colId}
                                    column={column}
                                  />
                                ) : (
                                  <>{row[colId]}</>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Expanded Row Content */}
                      <TableRow>
                        <TableCell
                          colSpan={
                            columns.length +
                            (selectable ? 1 : 0) +
                            (groupBy ? 1 : 0)
                          }
                          sx={{ padding: 0, borderBottom: "none" }}
                        >
                          <Collapse
                            in={!!expandedRows[row.id]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box margin={1}>
                              {renderExpandedContent &&
                                renderExpandedContent(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              )
            )}
          </TableBody>
        </Table>

        {paginated && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
    </DndProvider>
  );
};

export default TableComponent;